"use client";

import React, { useState } from "react";
import { BookOpen, Clock, Users, CircleCheck as CheckCircle2, ArrowRight, ShieldCheck, Building2, FileText, Landmark, Scale } from 'lucide-react';
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
const programs = [{
  id: "conac",
  title: "Armonización contable y normativa CONAC",
  badge: "Sector público",
  audience: "Tesoreros, directores de contabilidad y órganos de control",
  duration: "24 horas (teórico-práctico)",
  deliverables: "Manual de lineamientos contables, catálogo de cuentas homologado y cédulas de registro patrimonial.",
  iconName: "landmark"
}, {
  id: "ldf",
  title: "Ley de disciplina financiera y presupuesto",
  badge: "Finanzas públicas",
  audience: "Administradores municipales, secretarios de finanzas y analistas",
  duration: "20 horas de sesión directiva",
  deliverables: "Matrices de indicadores presupuestales, proyecciones multianuales y semáforo de alertas financieras.",
  iconName: "scale"
}, {
  id: "auditoria",
  title: "Control interno y auditoría preventiva",
  badge: "Cumplimiento",
  audience: "Contralorías internas, directivos de área y auditores preventivos",
  duration: "18 horas técnicas",
  deliverables: "Matriz de riesgos institucionales, mapa de procesos críticos y formato de solventación preventiva.",
  iconName: "shield"
}, {
  id: "cuenta-publica",
  title: "Fiscalización de la cuenta pública",
  badge: "Rendición de cuentas",
  audience: "Servidores públicos responsables del ejercicio y comprobación del gasto",
  duration: "20 horas especializadas",
  deliverables: "Protocolo de integración documental, cédulas analíticas y guía de respuesta a pliegos de observaciones.",
  iconName: "file"
}, {
  id: "adquisiciones",
  title: "Adquisiciones y obra pública",
  badge: "Contratación",
  audience: "Comités de adquisiciones, áreas de compras públicas y contratistas",
  duration: "22 horas presenciales o en línea",
  deliverables: "Bases de licitación estándar, formatos de fallo y lista de verificación técnica de contratos.",
  iconName: "building"
}, {
  id: "fiscal-corp",
  title: "Actualización fiscal corporativa",
  badge: "Sector empresarial",
  audience: "Directores de finanzas corporativas, contadores generales y asesores",
  duration: "16 horas de actualización",
  deliverables: "Guía de criterios de deducción, diagnóstico de CFDI 4.0 y defensa fiscal preventiva.",
  iconName: "book"
}];
export default function Training() {
  const [activeProgramId, setActiveProgramId] = useState("conac");
  const shouldReduceMotion = useReducedMotion();
  const selectedProgram = programs.find(item => item.id === activeProgramId) || programs[0];
  const getProgramIcon = (name: string) => {
    switch (name) {
      case "landmark":
        return <Landmark className="size-5 text-primary" />;
      case "scale":
        return <Scale className="size-5 text-primary" />;
      case "shield":
        return <ShieldCheck className="size-5 text-primary" />;
      case "file":
        return <FileText className="size-5 text-primary" />;
      case "building":
        return <Building2 className="size-5 text-primary" />;
      case "book":
      default:
        return <BookOpen className="size-5 text-primary" />;
    }
  };
  return <section id="programas-de-capacitación" data-nav="light" className="bg-background text-foreground py-20 lg:py-28">
      <div className="mx-auto max-w-7xl px-6 md:px-10 lg:px-16">
        {/* Header Section */}
        <div className="max-w-3xl mb-14 lg:mb-20">
          <div className="inline-flex items-center gap-2 border border-ring/40 bg-card px-3 py-1 text-xs font-semibold tracking-wider text-foreground uppercase rounded-xs mb-4">
            <span>Formación institucional</span>
            <span className="text-ring font-bold">·</span>
            <span>Gobierno y empresa</span>
          </div>

          <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-foreground uppercase leading-tight">
            Capacitación directiva y técnica para su equipo
          </h2>

          <div className="w-12 h-0.5 bg-ring mt-4 mb-5" />

          <p className="text-muted-foreground text-base sm:text-lg leading-relaxed">
            Actualice a sus funcionarios y personal corporativo mediante
            programas enfocados en normatividad vigente, rigor contable y
            prevención legal con metodologías aplicadas en campo.
          </p>
        </div>

        {/* 2-Column Main Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 items-start">
          {/* Left Column: Workshop Context + Photo Card */}
          <motion.div initial={shouldReduceMotion ? false : {
          opacity: 0,
          y: 16
        }} whileInView={shouldReduceMotion ? undefined : {
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5,
          ease: "easeOut"
        }} className="lg:col-span-5 flex flex-col gap-6">
            {/* Visual workshop card */}
            <div className="relative overflow-hidden rounded-xs border border-border bg-card shadow-sm">
              <div className="relative aspect-4/3 w-full bg-muted overflow-hidden">
                <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1799246/img/training_workshop_executives.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1799246/img/training_workshop_executives.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1799246/img/training_workshop_executives.png-wvc-sizes" alt="Sesión de capacitación institucional para directivos y funcionarios públicos" loading="lazy" decoding="async" className="w-full h-full object-cover" />
                <div className="absolute inset-0 bg-primary/20 pointer-events-none" />
                <div className="absolute bottom-3 left-3 bg-secondary text-secondary-foreground text-xs px-3 py-1.5 font-serif font-semibold tracking-wider uppercase border border-ring/30 rounded-xs">
                  Modalidad presencial y virtual
                </div>
              </div>

              <div className="p-6 sm:p-8 flex flex-col gap-5">
                <div>
                  <h3 className="font-serif text-lg font-bold text-foreground uppercase tracking-wide">
                    Enfoque práctico garantizado
                  </h3>
                  <p className="text-muted-foreground text-sm leading-relaxed mt-2">
                    Cada programa es impartido por especialistas senior que
                    combinan experiencia docente con trabajo activo en auditoría,
                    administración hacendaria y litigio público.
                  </p>
                </div>

                <Separator className="bg-border" />

                <div className="grid grid-cols-2 gap-4 text-left">
                  <div className="border-l-2 border-ring pl-3">
                    <span className="block font-serif text-xl font-bold text-foreground">
                      100%
                    </span>
                    <span className="text-xs text-muted-foreground">
                      Alineado a normas oficiales
                    </span>
                  </div>
                  <div className="border-l-2 border-ring pl-3">
                    <span className="block font-serif text-xl font-bold text-foreground">
                      Constancia
                    </span>
                    <span className="text-xs text-muted-foreground">
                      Valor curricular institucional
                    </span>
                  </div>
                </div>

                <div className="pt-2">
                  <Button asChild className="w-full h-11 bg-primary text-primary-foreground border border-ring font-serif text-xs uppercase tracking-widest font-semibold hover:bg-accent hover:text-accent-foreground transition-colors rounded-xs">
                    <Link to="/ContactPage">
                      <span>Solicitar propuesta institucional</span>
                      <ArrowRight className="size-4 ml-1.5 text-ring" />
                    </Link>
                  </Button>
                </div>
              </div>
            </div>

            {/* Direct Contact reassurance */}
            <div className="bg-card border border-border p-5 rounded-xs flex items-center justify-between">
              <div>
                <p className="text-xs uppercase tracking-wider text-muted-foreground font-semibold">
                  Atención directa
                </p>
                <p className="text-sm font-medium text-foreground mt-0.5">
                  javilez@avsconsultores.org
                </p>
              </div>
              <Button asChild variant="outline" size="sm" className="border-border text-foreground hover:bg-muted font-serif text-xs uppercase tracking-wider">
                <Link to="/ContactPage">Consultar</Link>
              </Button>
            </div>
          </motion.div>

          {/* Right Column: Syllabus & Programs Browser */}
          <motion.div initial={shouldReduceMotion ? false : {
          opacity: 0,
          y: 16
        }} whileInView={shouldReduceMotion ? undefined : {
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5,
          delay: 0.1,
          ease: "easeOut"
        }} className="lg:col-span-7 flex flex-col gap-6">
            {/* Quick list of programs */}
            <div className="space-y-3">
              <div className="flex items-center justify-between mb-1">
                <span className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
                  Catálogo de programas disponibles
                </span>
                <span className="text-xs text-muted-foreground">
                  Seleccione un programa para ver detalles
                </span>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {programs.map((prog, i) => {
                const isSelected = prog.id === activeProgramId;
                return <div key={prog.id} data-index={i} onClick={() => setActiveProgramId(prog.id)} onKeyDown={e => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    setActiveProgramId(prog.id);
                  }
                }} role="button" tabIndex={0} className={`text-left p-4 rounded-xs border cursor-pointer transition-colors duration-200 ${isSelected ? "bg-card border-ring shadow-xs" : "bg-background border-border hover:border-ring/40 hover:bg-card/50"}`}>
                      <div className="flex items-start gap-3">
                        <div className="p-2 bg-background border border-border rounded-xs shrink-0 mt-0.5">
                          {getProgramIcon(prog.iconName)}
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="inline-block text-[10px] font-bold uppercase tracking-wider text-muted-foreground border border-border px-1.5 py-0.5 rounded-xs mb-1">
                            {prog.badge}
                          </div>
                          <h4 className="font-serif text-sm font-semibold text-foreground leading-snug line-clamp-2">
                            {prog.title}
                          </h4>
                          <div className="flex items-center gap-1.5 text-xs text-muted-foreground mt-2">
                            <Clock className="size-3.5 text-ring shrink-0" />
                            <span className="truncate">{prog.duration}</span>
                          </div>
                        </div>
                      </div>
                    </div>;
              })}
              </div>
            </div>

            {/* Selected Syllabus Dossier Card */}
            <Card className="rounded-xs border border-ring/50 bg-card shadow-sm mt-2 relative overflow-hidden">
              <div className="absolute top-0 left-0 right-0 h-0.5 bg-ring" />
              <CardHeader className="pb-4">
                <div className="flex items-center justify-between gap-4 flex-wrap">
                  <span className="inline-flex items-center gap-1.5 text-xs font-serif font-bold uppercase tracking-widest text-foreground bg-accent px-2.5 py-1 border border-ring/40 rounded-xs">
                    <CheckCircle2 className="size-3.5 text-ring" />
                    {selectedProgram.badge}
                  </span>
                  <div className="flex items-center gap-1.5 text-xs font-semibold text-muted-foreground">
                    <Clock className="size-3.5 text-ring" />
                    <span>{selectedProgram.duration}</span>
                  </div>
                </div>

                <CardTitle className="font-serif text-xl sm:text-2xl font-bold uppercase tracking-wide text-foreground mt-3">
                  {selectedProgram.title}
                </CardTitle>
                <CardDescription className="text-sm text-muted-foreground">
                  Estructura académica orientada a resultados y solventación institucional.
                </CardDescription>
              </CardHeader>

              <CardContent className="space-y-5">
                <Separator className="bg-border" />

                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <Users className="size-4 text-ring" />
                    <span className="font-serif text-xs font-bold uppercase tracking-wider text-foreground">
                      Perfil y audiencia objetivo
                    </span>
                  </div>
                  <p className="text-sm text-muted-foreground leading-relaxed pl-6">
                    {selectedProgram.audience}
                  </p>
                </div>

                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <FileText className="size-4 text-ring" />
                    <span className="font-serif text-xs font-bold uppercase tracking-wider text-foreground">
                      Entregables y documentación incluida
                    </span>
                  </div>
                  <p className="text-sm text-muted-foreground leading-relaxed pl-6">
                    {selectedProgram.deliverables}
                  </p>
                </div>

                <div className="pt-3 flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
                  <Button asChild className="h-10 bg-primary text-primary-foreground font-serif text-xs uppercase tracking-wider font-semibold border border-ring hover:bg-accent hover:text-accent-foreground transition-colors rounded-xs flex-1">
                    <Link to="/ContactPage">
                      Solicitar temario completo
                    </Link>
                  </Button>
                  <Button asChild variant="outline" className="h-10 border-border text-foreground font-serif text-xs uppercase tracking-wider hover:bg-muted transition-colors rounded-xs">
                    <Link to="/OnlineConsultingPage">
                      Asesoría previa
                    </Link>
                  </Button>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        </div>
      </div>
    </section>;
}